"use client";

import React, { useEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";
import * as echarts from "echarts";

interface SessionChartProps {
  data?: Array<{ month: string; count: number }>;
  height?: string | number;
}

const SessionChart: React.FC<SessionChartProps> = ({ 
  data = [
    { month: "Jan", count: 18 },
    { month: "Feb", count: 22 },
    { month: "Mar", count: 26 },
    { month: "Apr", count: 28 },
    { month: "May", count: 30 },
    { month: "Jun", count: 32 }
  ],
  height = "100%" 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Chart options
    const options = {
      grid: {
        top: 15,
        right: 10,
        bottom: 30,
        left: 10,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        formatter: '{b}: {c}'
      },
      xAxis: {
        type: 'category',
        data: data.map(item => item.month),
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: '#E2E8F0'
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#E2E8F0',
            type: 'dashed',
            opacity: 0.3
          }
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 45,
        interval: 15,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#E2E8F0',
            opacity: 0.5
          }
        }
      },
      series: [
        {
          name: 'Session Count',
          type: 'line',
          data: data.map(item => item.count),
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#6366F1'
          },
          lineStyle: {
            width: 2,
            color: '#6366F1'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(99, 102, 241, 0.3)'
              },
              {
                offset: 1,
                color: 'rgba(99, 102, 241, 0.0)'
              }
            ])
          }
        }
      ]
    };

    // Set options and render chart
    chartInstance.current.setOption(options);

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [data]);

  return (
    <Box 
      ref={chartRef} 
      width="100%" 
      height={height}
      borderRadius="md"
    />
  );
};

export default SessionChart; 